/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { useEffect } from "react";
import { useLocation } from "react-router";

// TODO: Remove this component after react-router update and data router migration

/**
 * This component makes sure that the element is on the page (fully loaded) when switching pages with hashes.
 * The problem it resolves is: When navigating from page A to page B#anchor, because the SPA can take some time to fully
 * render the new page, the element can initially be missing. This component retries for a specified time to find that element.
 * If it cannot be found after this, it does nothing.
 */
export const ScrollToHash = () => {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!hash) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;
    let retries = 0;
    const retryInterval = 50;
    const maxRetries = 5000 / retryInterval; // 100 retries = 5 seconds

    const tryScroll = () => {
      const id = decodeURIComponent(hash).replace("#", "");
      const element = document.getElementById(id);

      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        element.setAttribute("tabIndex", "-1");
        element.focus({ preventScroll: true });
      } else if (retries < maxRetries) {
        retries++;
        timeoutId = setTimeout(tryScroll, retryInterval);
      }
    };

    // Only runs after the DOM is updated, pushes into event queue
    const tryScrollTimeout = setTimeout(tryScroll, 0);

    return () => {
      clearTimeout(tryScrollTimeout);
      clearTimeout(timeoutId);
    };
  }, [hash, pathname]);

  return null;
};
